import type { SessionId } from "@don/wire";

/**
 * Search across transcripts and Summaries, as far as the Console can see it (issue 26).
 *
 * **The Console cannot search a transcript, and this module is the shape of that fact.** A
 * row carries a person, a title, a shape, a count and four dates; what was said during the
 * session is not on it and must not be, so a word spoken and never written down can only be
 * matched by the database that holds the corpus. The Console asks a {@link SearchSource} and
 * narrows the list it already has by the answer.
 *
 * What stays here is the product rule: a row survives the search box when the strings it
 * carries match **or** the corpus named its session. The union is the point — the corpus
 * covers the title and the Summary, and the row carries the person, who is not in the
 * corpus at all because a person is a filter rather than something somebody said.
 */

/**
 * Where a corpus answer comes from. `POST /sessions/search` with `{"q": …}` is what fills
 * this in a deployment; a test fills it with the answer the case needs.
 *
 * It is a port and not a `fetch` written inline for the reason `App` owning `rows` is one:
 * the Console has no data source yet, and a module that called an endpoint would be
 * asserting against a contract nothing serves.
 *
 * **It can reject, and the screen has to be able to say so.** A search whose request failed
 * is not a search that found nothing — see {@link CorpusReply}.
 */
export type SearchSource = (query: string) => Promise<readonly SessionId[]>;

/**
 * How long the box waits before asking, in milliseconds.
 *
 * One request per pause rather than one per keystroke: typing a ten-character word sent ten
 * requests, nine of which the staleness guard then discarded, and in a deployment every one
 * of them is a `websearch_to_tsquery` over the corpus. It is a trailing wait, so the ask is
 * for the whole word rather than for its prefixes. It does **not** close the window the
 * screen has to be honest about — a single round trip is still a round trip — which is what
 * {@link corpusStanding} is for.
 */
export const SEARCH_DEBOUNCE_MS = 200;

/**
 * One corpus answer, and the query it answered.
 *
 * **The query is carried rather than assumed, and that is the whole of the staleness
 * guard.** A source is asynchronous, so answers can arrive out of order or long after the
 * owner has typed past them. Rather than a sequence counter in a component — a race handled
 * by a variable nothing can test — an answer is only ever applied to the query it was asked
 * for, which is a comparison a pure function makes. An answer for a query that is no longer
 * in the box narrows nothing, and the box falls back to the strings the rows carry until the
 * right answer lands.
 */
export interface CorpusAnswer {
  readonly query: string;
  readonly sessionIds: readonly SessionId[];
}

/**
 * What the corpus has said, as the screen holds it.
 *
 * **`CorpusAnswer | null` could not tell three situations apart, and the screen rendered all
 * three as a settled "no session matches this filter".** Nothing asked, an answer in flight,
 * and a request that failed are different facts about the same box, and only the first two
 * share a screen with anything. So the reply is the state, and {@link CorpusAnswer} — the
 * thing `applyFilters` narrows by — is derived from it by {@link corpusAnswer}: a reply that
 * is not an answer narrows nothing, which is the same fallback `null` always had.
 *
 * `query` is on every variant for the reason it was on the answer: it is the whole of the
 * staleness guard, and a reply for a query the owner has typed past is not about the box in
 * front of them.
 */
export type CorpusReply =
  | { readonly state: "answered"; readonly query: string; readonly sessionIds: readonly SessionId[] }
  | { readonly state: "unreachable"; readonly query: string };

/**
 * What the Console can honestly say about the corpus for the query now in the box.
 *
 * - `unasked` — there is no corpus to ask, or nothing to ask it. The box is the two strings
 *   a row carries and nothing is pending.
 * - `asking` — a query is in the box and the answer for **that** query is not back. This is
 *   most of a search's life, it is the state an owner types through, and it is the one that
 *   was being rendered as a finished negative.
 * - `answered` — the corpus has answered this query.
 * - `unreachable` — the corpus was asked and the request failed. A search that could not run
 *   is not a search that found nothing, and the screen must not say the second.
 */
export type CorpusStanding = "unasked" | "asking" | "answered" | "unreachable";

/**
 * Which of the four a screen is in, as arithmetic rather than as a flag a component sets.
 *
 * **Anything that is not an answer to the query in the box is `asking`**, including the frame
 * before the effect has run and the whole of {@link SEARCH_DEBOUNCE_MS}. That is deliberate:
 * the honest statement during those is "we have not been told yet", and deriving it from the
 * query rather than from a timer means there is no window in which the screen believes it has
 * a finished answer it does not have.
 */
export function corpusStanding(
  reply: CorpusReply | null,
  query: string,
  hasSource: boolean,
): CorpusStanding {
  const asked = normaliseQuery(query);
  if (!hasSource || asked.length === 0) return "unasked";
  if (reply === null || normaliseQuery(reply.query) !== asked) return "asking";
  return reply.state;
}

/** The rows a reply names, or `null` where it names none — see {@link corpusNamed}. */
export function corpusAnswer(reply: CorpusReply | null): CorpusAnswer | null {
  if (reply === null || reply.state !== "answered") return null;
  return { query: reply.query, sessionIds: reply.sessionIds };
}

/** What the owner actually typed, with the whitespace that cannot be searched removed. */
export function normaliseQuery(query: string): string {
  return query.trim();
}

/**
 * Whether the corpus named this session for this query.
 *
 * `null` — no source configured, or no answer back yet — is not "no match": it is "nothing
 * has been said about the corpus", and the caller falls back to the strings it holds.
 */
export function corpusNamed(
  answer: CorpusAnswer | null,
  query: string,
  sessionId: SessionId,
): boolean {
  if (answer === null) return false;
  if (answer.query !== normaliseQuery(query)) return false;
  return answer.sessionIds.includes(sessionId);
}

/**
 * The search field's placeholder, and its accessible name (design-spec §4, issue 194).
 *
 * **A placeholder is a promise** (issue 25's Decision 11). The field is drawn only where a
 * corpus source is configured (`LibraryScreen`), so the promise it makes, sessions searched by
 * what was said in them, is true wherever it is shown. With no source there is no field.
 */
export const SEARCH_PLACEHOLDER = "Search sessions";

/**
 * What the screen says about the corpus while it is not answering, or `null` when there is
 * nothing to say (screen catalog `sessions-search-asking`, `sessions-search-unreachable`).
 *
 * **Two sentences that exist so a third one stops being told.** A no-match is a statement
 * about a finished search, and it was being shown during every unfinished one and every
 * failed one, with a control beside it that throws away the query that was never answered.
 * These say which of the three it is, and the copy is a function rather than a string in a
 * render because a sentence the product says is worth a test.
 */
export function searchNotice(standing: CorpusStanding): string | null {
  if (standing === "asking") return "Searching transcripts and summaries";
  if (standing === "unreachable") {
    return "Showing title and person matches only. Transcripts and summaries couldn't be searched.";
  }
  return null;
}

/** The finished search that found nothing (screen catalog `sessions-search-no-match`). */
export function noMatchLine(query: string): string {
  return `No session mentions "${normaliseQuery(query)}" in its title, transcript or summary.`;
}

/**
 * A title cut into runs, each marked where it matches the query (issue 194): every
 * case-insensitive occurrence of the trimmed query, left to right, without overlap. A blank
 * query marks nothing. The runs join back to the title exactly.
 */
export function markMatches(
  text: string,
  query: string,
): { readonly text: string; readonly match: boolean }[] {
  if (text.length === 0) return [];
  const needle = normaliseQuery(query).toLowerCase();
  const haystack = text.toLowerCase();
  // A character whose lower case is longer (`İ`) would shift every index after it, so such a
  // title is left unmarked rather than marked in the wrong place.
  if (needle.length === 0 || haystack.length !== text.length) return [{ text, match: false }];
  const runs: { text: string; match: boolean }[] = [];
  let from = 0;
  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, from)) {
    if (at > from) runs.push({ text: text.slice(from, at), match: false });
    runs.push({ text: text.slice(at, at + needle.length), match: true });
    from = at + needle.length;
  }
  if (from < text.length) runs.push({ text: text.slice(from), match: false });
  return runs;
}
